import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { funeralHomeNameOf } from './adminBugoStat.util';
import { OrderStatKind } from './adminOrderStat.types';
import {
  PenetrationBase,
  PenetrationBrandRow,
  PenetrationFeventRow,
  PenetrationResult,
} from './adminPenetration.types';
//* 분모·금액 규칙은 화면 표와 한 곳에서 공유한다 (엑셀만 따로 계산하면 숫자가 갈린다)
import { denomOf, metricsOf } from './adminPenetration.util';

//* 프로젝트 표준(커뮤니티 xlsx + file-saver). 스타일 플러그인은 쓰지 않고
//* 너비/고정/필터/숫자포맷으로만 가독성을 낸다.

const charWidth = (value: unknown): number => {
  const text = value === null || value === undefined ? '' : String(value);
  //* 한글은 영문보다 넓다 — 대략 2배로 잡는다
  // eslint-disable-next-line no-control-regex
  return text.split('').reduce((sum, ch) => sum + (/[^\x00-\xff]/.test(ch) ? 2 : 1), 0);
};

const autoWidth = (rows: (string | number | null)[][]): XLSX.ColInfo[] => {
  const widths: number[] = [];
  rows.forEach((row) => {
    row.forEach((cell, index) => {
      widths[index] = Math.max(widths[index] ?? 0, charWidth(cell));
    });
  });
  return widths.map((w) => ({ wch: Math.min(Math.max(w + 2, 6), 40) }));
};

/** 헤더 고정 — 커뮤니티 xlsx는 !views까지 있어야 실제로 고정된다 */
const freezeHeader = (ws: XLSX.WorkSheet): void => {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  ws['!views'] = [{ state: 'frozen', ySplit: 1 }] as XLSX.WorkSheet['!views'];
};

const round = (value: number) => Math.round(value);

/** 비율은 소수 1자리 숫자로 — 엑셀에서 그대로 정렬·비교할 수 있게 %문자는 안 붙인다 */
const rate1 = (a: number, b: number): number | null =>
  b > 0 ? Math.round((a / b) * 1000) / 10 : null;

export const exportPenetrationExcel = (
  kind: OrderStatKind,
  base: PenetrationBase,
  result: PenetrationResult,
  rows: PenetrationFeventRow[],
  period: { from: string; to: string },
): void => {
  const kindLabel = kind === 'gift' ? '답례품' : '화환';
  const denomLabel = base === 'shared' ? '공유상가' : '발인상가';

  //* 화면에 보이는 기준선 그대로 — 엑셀에서 다시 계산하면 숫자가 갈린다
  const totalDenom = denomOf(result.totals, base);
  const baseline = totalDenom.total > 0 ? totalDenom.buying / totalDenom.total : null;

  const header = [
    '상조',
    denomLabel,
    '구매상가',
    '구매율(%)',
    '평균대비(%)',
    '발인상가',
    '공유상가',
    '주문',
    '금액',
    '구매단가',
    '구매상가당금액',
    '상가당건수',
    `${denomLabel}당금액`,
    '수량',
  ];

  const brandRowOf = (row: Omit<PenetrationBrandRow, 'key' | 'brandName'> & {
    brandName?: string;
  }) => {
    const { total, buying } = denomOf(row, base);
    const rate = total > 0 ? buying / total : null;
    //* 금액·건수도 분모와 같은 모수에서 (공유 분모면 공유 상가 주문만)
    const { orderCount, amount, quantity } = metricsOf(row, base);
    return [
      row.brandName ?? '합계',
      total,
      buying,
      rate1(buying, total),
      baseline && rate !== null ? Math.round((rate / baseline) * 100) : null,
      row.feventCount,
      row.sharedFeventCount,
      orderCount,
      round(amount),
      orderCount > 0 ? round(amount / orderCount) : null,
      buying > 0 ? round(amount / buying) : null,
      buying > 0 ? Math.round((orderCount / buying) * 10) / 10 : null,
      total > 0 ? round(amount / total) : null,
      quantity,
    ];
  };

  const brandRows: (string | number | null)[][] = [
    header,
    ...result.brands.map((brand) => brandRowOf(brand)),
    brandRowOf(result.totals),
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(brandRows);
  ws['!cols'] = autoWidth(brandRows);
  freezeHeader(ws);
  //* 합계행은 필터 범위에서 제외
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: brandRows.length - 2, c: header.length - 1 },
    }),
  };
  XLSX.utils.book_append_sheet(wb, ws, '상조별');

  //* 상가별 시트 — 미구매 상가를 그대로 뽑아 영업 목록으로 쓰라고 구매여부를 첫 칸 근처에 둔다
  const feventHeader = [
    '발인일',
    '상조',
    '고인',
    '장례식장',
    '구매여부',
    '주문',
    '금액',
    '수량',
    '공유',
  ];
  const feventRows: (string | number | null)[][] = [
    feventHeader,
    ...[...rows]
      .sort((a, b) => (a.coffinOutDate ?? '').localeCompare(b.coffinOutDate ?? ''))
      .map((row) => [
        row.coffinOutDate ?? '',
        row.brandName,
        row.fevent.deceasedInfo?.name ?? '',
        funeralHomeNameOf(row.fevent),
        row.orderCount > 0 ? '구매' : '미구매',
        row.orderCount,
        round(row.amount),
        row.quantity,
        row.shareCount,
      ]),
  ];
  const feventWs = XLSX.utils.aoa_to_sheet(feventRows);
  feventWs['!cols'] = autoWidth(feventRows);
  freezeHeader(feventWs);
  feventWs['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: feventRows.length - 1, c: feventHeader.length - 1 },
    }),
  };
  XLSX.utils.book_append_sheet(wb, feventWs, '상가별');

  const dayRows: (string | number | null)[][] = [
    [
      '발인일',
      denomLabel,
      '구매상가',
      '구매율(%)',
      '평균대비(%)',
      '발인상가',
      '주문',
      '금액',
      '구매단가',
    ],
    ...result.days.map((day) => {
      const { total, buying } = denomOf(day, base);
      const rate = total > 0 ? buying / total : null;
      const { orderCount, amount } = metricsOf(day, base);
      return [
        day.date,
        total,
        buying,
        rate1(buying, total),
        baseline && rate !== null ? Math.round((rate / baseline) * 100) : null,
        day.feventCount,
        orderCount,
        round(amount),
        orderCount > 0 ? round(amount / orderCount) : null,
      ];
    }),
  ];
  const dayWs = XLSX.utils.aoa_to_sheet(dayRows);
  dayWs['!cols'] = autoWidth(dayRows);
  freezeHeader(dayWs);
  XLSX.utils.book_append_sheet(wb, dayWs, '발인일별');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    `${kindLabel}구매율_${period.from}_${period.to}.xlsx`,
  );
};
